import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type RosterRow } from "../lib/api";

const FUNCTIONS = [
  "Engineering", "Sales", "HR", "Finance",
  "Project Management", "Operations", "Other",
];

export function Roster() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<RosterRow[]>({
    queryKey: ["roster"],
    queryFn: api.roster.list,
  });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", function: "Engineering" });

  const create = useMutation({
    mutationFn: () => api.roster.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      setAdding(false);
      setForm({ name: "", email: "", function: "Engineering" });
    },
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.roster.update(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roster"] }),
  });

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-600">Failed to load roster: {String(error)}</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Roster</h1>
        <button
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
          onClick={() => setAdding((s) => !s)}
        >
          {adding ? "Cancel" : "Add person"}
        </button>
      </div>

      {adding ? (
        <form
          className="flex gap-2 items-end border rounded p-3 bg-white"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="flex-1">
            <label className="block text-xs text-slate-600">Name</label>
            <input
              className="border rounded px-2 py-1 text-sm w-full"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-slate-600">Email</label>
            <input
              type="email"
              className="border rounded px-2 py-1 text-sm w-full"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600">Function</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={form.function}
              onChange={(e) => setForm({ ...form, function: e.target.value })}
            >
              {FUNCTIONS.map((f) => <option key={f}>{f}</option>)}
            </select>
          </div>
          <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Add</button>
        </form>
      ) : null}

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300">
          <tr>
            <th className="text-left py-2">Name</th>
            <th className="text-left">Email</th>
            <th className="text-left">Function</th>
            <th className="text-left">Roles</th>
            <th className="text-right">Active</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((r) => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="py-2">{r.name}</td>
              <td className="text-slate-600">{r.email}</td>
              <td>{r.function}</td>
              <td className="text-slate-600">
                {rolesOf(r).join(", ") || "—"}
              </td>
              <td className="text-right">
                <button
                  className="text-xs underline text-slate-600"
                  onClick={() => toggleActive.mutate({ id: r.id, active: !r.active })}
                >
                  {r.active ? "Deactivate" : "Reactivate"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function rolesOf(r: RosterRow): string[] {
  const out: string[] = [];
  if (r.is_pilot) out.push("pilot");
  if (r.is_manager) out.push("manager");
  if (r.is_champion) out.push("champion");
  if (r.is_strategist) out.push("strategist");
  if (r.is_ceo) out.push("ceo");
  return out;
}
